import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SupplierLayout } from '../../components/SupplierLayout';
import { useSupplierStore, Truck as TruckType } from '../../lib/supplierStore';
import { Truck, Plus, MoreVertical, X, Edit2, Trash2, UserPlus, Wrench, Power, Search, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
const TRUCK_TYPES = ['1.5-Ton Pickup', '3-Ton Box Truck', '5-Ton Curtain-side', 'Flatbed 12m', 'Flatbed recovery', 'Low-bed 40-Ton', 'Refrigerated 3-Ton', 'Refrigerated 7-Ton', 'Tipper / Dump Truck', 'Tanker'];
const STATUS_OPTIONS: TruckType['status'][] = ['Active', 'In service', 'Idle', 'Maintenance'];
export function SupplierTrucks() {
  const {
    trucks,
    drivers,
    addTruck,
    updateTruck,
    deleteTruck
  } = useSupplierStore();
  const [modalMode, setModalMode] = useState<'add' | 'edit' | null>(null);
  const [editingTruck, setEditingTruck] = useState<TruckType | null>(null);
  const [assignTruck, setAssignTruck] = useState<TruckType | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TruckType | null>(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const filteredTrucks = useMemo(() => {
    return trucks.filter(t => {
      const matchesSearch = search === '' || t.plate.toLowerCase().includes(search.toLowerCase()) || t.type.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [trucks, search, statusFilter]);
  const handleOpenAdd = () => {
    setEditingTruck(null);
    setModalMode('add');
  };
  const handleOpenEdit = (truck: TruckType) => {
    setEditingTruck(truck);
    setModalMode('edit');
  };
  const handleCloseModal = () => {
    setModalMode(null);
    setEditingTruck(null);
  };
  const handleSave = (data: Omit<TruckType, 'id'>) => {
    if (modalMode === 'edit' && editingTruck) {
      updateTruck(editingTruck.id, data);
      toast.success(`Truck ${data.plate} updated`);
    } else {
      addTruck(data);
      toast.success(`Truck ${data.plate} added to fleet`);
    }
    handleCloseModal();
  };
  const handleDelete = () => {
    if (!confirmDelete) return;
    deleteTruck(confirmDelete.id);
    toast.success(`Truck ${confirmDelete.plate} removed from fleet`);
    setConfirmDelete(null);
  };
  const handleAssignDriver = (truckId: string, driverId: string | null) => {
    updateTruck(truckId, {
      assignedDriverId: driverId || undefined
    });
    const driverName = driverId ? drivers.find(d => d.id === driverId)?.name : null;
    toast.success(driverName ? `Assigned ${driverName}` : 'Driver removed from truck');
    setAssignTruck(null);
  };
  const handleChangeStatus = (truck: TruckType, status: TruckType['status']) => {
    updateTruck(truck.id, {
      status
    });
    toast.success(`Status set to ${status}`);
  };
  return <SupplierLayout data-id="element-4417">
      <div className="max-w-6xl mx-auto" data-id="element-4418">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4" data-id="element-4419">
          <div data-id="element-4420">
            <h1 className="text-2xl font-display font-bold text-gray-900" data-id="element-4421">
              Fleet Management
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-4422">
              {trucks.length} total vehicle{trucks.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-4423">
            <button onClick={() => setBulkUploadOpen(true)} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 rounded-xl text-sm font-bold transition-colors shadow-sm" data-id="element-4424">
              <Upload className="w-4 h-4" data-id="element-4425" /> Bulk Upload
            </button>
            <button onClick={handleOpenAdd} className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-bold transition-colors shadow-sm" data-id="element-4426">
              <Plus className="w-4 h-4" data-id="element-4427" /> Add Truck
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6" data-id="element-4428">
          <div className="relative flex-1" data-id="element-4429">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-4430" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by plate or type..." className="w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-gray-900 focus:border-gray-900" data-id="element-4431" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-gray-900 focus:border-gray-900" data-id="element-4432">
            <option value="all" data-id="element-4433">All statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s} data-id="element-4434">
                {s}
              </option>)}
          </select>
        </div>

        {filteredTrucks.length === 0 ? <div className="bg-white rounded-2xl p-12 border border-gray-100 text-center" data-id="element-4435">
            <Truck className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-4436" />
            <p className="text-sm font-medium text-gray-900" data-id="element-4437">No trucks found</p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-4438">
              {trucks.length === 0 ? 'Add your first truck to start managing your fleet.' : 'Try adjusting your search or filters.'}
            </p>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4439">
            {filteredTrucks.map(truck => {
          const driver = drivers.find(d => d.id === truck.assignedDriverId);
          return <TruckCard key={truck.id} truck={truck} driverName={driver?.name} onEdit={() => handleOpenEdit(truck)} onAssignDriver={() => setAssignTruck(truck)} onChangeStatus={status => handleChangeStatus(truck, status)} onDelete={() => setConfirmDelete(truck)} data-id="element-4440" />;
        })}
          </div>}
      </div>

      {/* Add / Edit Modal */}
      <AnimatePresence data-id="element-4441">
        {modalMode && <TruckFormModal mode={modalMode} initial={editingTruck} drivers={drivers} trucks={trucks} onClose={handleCloseModal} onSave={handleSave} data-id="element-4442" />}
        {assignTruck && <AssignDriverModal truck={assignTruck} drivers={drivers} trucks={trucks} onClose={() => setAssignTruck(null)} onAssign={driverId => handleAssignDriver(assignTruck.id, driverId)} data-id="element-4443" />}
        {confirmDelete && <ConfirmDeleteModal truck={confirmDelete} onCancel={() => setConfirmDelete(null)} onConfirm={handleDelete} data-id="element-4444" />}
        {bulkUploadOpen && <BulkUploadModal onClose={() => setBulkUploadOpen(false)} onImport={rows => {
        let count = 0;
        rows.forEach(row => {
          addTruck({
            plate: row.plate,
            type: row.type,
            capacity: row.capacity,
            status: row.status,
            lastService: row.lastService
          });
          count++;
        });
        toast.success(`${count} truck${count !== 1 ? 's' : ''} imported successfully`);
        setBulkUploadOpen(false);
      }} data-id="element-4445" />}
      </AnimatePresence>
    </SupplierLayout>;
}
// ============ Truck Card with Kebab Menu ============
function TruckCard({
  truck,
  driverName,
  onEdit,
  onAssignDriver,
  onChangeStatus,
  onDelete
}: {
  truck: TruckType;
  driverName?: string;
  onEdit: () => void;
  onAssignDriver: () => void;
  onChangeStatus: (status: TruckType['status']) => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setStatusMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);
  const statusBadgeClass = truck.status === 'Active' ? 'bg-green-100 text-green-700' : truck.status === 'In service' ? 'bg-blue-100 text-blue-700' : truck.status === 'Idle' ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-700';
  return <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm relative" data-id="element-4446">
      <div ref={menuRef} className="absolute top-4 right-4" data-id="element-4447">
        <button onClick={() => setMenuOpen(!menuOpen)} className="p-1.5 text-gray-400 hover:text-gray-900 rounded-lg hover:bg-gray-50 transition-colors" aria-label="Truck options" data-id="element-4448">
          <MoreVertical className="w-5 h-5" data-id="element-4449" />
        </button>
        <AnimatePresence data-id="element-4450">
          {menuOpen && <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: -4
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: -4
        }} transition={{
          duration: 0.12
        }} className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-20" data-id="element-4451">
              <button onClick={() => {
            setMenuOpen(false);
            onEdit();
          }} className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-4452">
                <Edit2 className="w-4 h-4 text-gray-400" data-id="element-4453" /> Edit truck
              </button>
              <button onClick={() => {
            setMenuOpen(false);
            onAssignDriver();
          }} className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-4454">
                <UserPlus className="w-4 h-4 text-gray-400" data-id="element-4455" />
                {driverName ? 'Reassign driver' : 'Assign driver'}
              </button>

              <div className="relative" data-id="element-4456">
                <button onClick={() => setStatusMenuOpen(!statusMenuOpen)} className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-4457">
                  <Power className="w-4 h-4 text-gray-400" data-id="element-4458" /> Change status
                </button>
                <AnimatePresence data-id="element-4459">
                  {statusMenuOpen && <motion.div initial={{
                opacity: 0,
                x: -4
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -4
              }} className="bg-gray-50 border-t border-b border-gray-100" data-id="element-4460">
                      {STATUS_OPTIONS.map(status => <button key={status} onClick={() => {
                  onChangeStatus(status);
                  setStatusMenuOpen(false);
                  setMenuOpen(false);
                }} className={`w-full text-left flex items-center justify-between gap-2 pl-9 pr-3 py-2 text-sm hover:bg-gray-100 ${truck.status === status ? 'font-bold text-gray-900' : 'text-gray-600'}`} data-id="element-4461">
                          {status}
                          {truck.status === status && <span className="text-[10px] text-brand-600 font-bold" data-id="element-4462">
                              ✓
                            </span>}
                        </button>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              <button onClick={() => {
            setMenuOpen(false);
            onChangeStatus('Maintenance');
          }} className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-4463">
                <Wrench className="w-4 h-4 text-gray-400" data-id="element-4464" /> Send to maintenance
              </button>
              <div className="border-t border-gray-100" data-id="element-4465">
                <button onClick={() => {
              setMenuOpen(false);
              onDelete();
            }} className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50" data-id="element-4466">
                  <Trash2 className="w-4 h-4" data-id="element-4467" /> Remove truck
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="flex items-center gap-4 mb-4" data-id="element-4468">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${truck.status === 'Active' ? 'bg-brand-50 text-brand-600' : 'bg-gray-100 text-gray-500'}`} data-id="element-4469">
          <Truck className="w-6 h-6" data-id="element-4470" />
        </div>
        <div className="min-w-0" data-id="element-4471">
          <h3 className="font-bold text-gray-900 text-lg truncate" data-id="element-4472">
            {truck.plate}
          </h3>
          <p className="text-xs text-gray-500 truncate" data-id="element-4473">
            {truck.type} • {truck.capacity}
          </p>
        </div>
      </div>

      <div className="space-y-3 pt-4 border-t border-gray-100" data-id="element-4474">
        <div className="flex justify-between items-center text-sm" data-id="element-4475">
          <span className="text-gray-500" data-id="element-4476">Status</span>
          <span className={`font-bold text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md ${statusBadgeClass}`} data-id="element-4477">
            {truck.status}
          </span>
        </div>
        <div className="flex justify-between items-center text-sm" data-id="element-4478">
          <span className="text-gray-500" data-id="element-4479">Driver</span>
          <span className="font-medium text-gray-900 truncate ml-2" data-id="element-4480">
            {driverName || <span className="text-gray-400 italic" data-id="element-4481">Unassigned</span>}
          </span>
        </div>
        <div className="flex justify-between items-center text-sm" data-id="element-4482">
          <span className="text-gray-500" data-id="element-4483">Last Service</span>
          <span className="font-medium text-gray-900" data-id="element-4484">{truck.lastService}</span>
        </div>
      </div>
    </div>;
}
// ============ Add / Edit Modal ============
function TruckFormModal({
  mode,
  initial,
  drivers,
  trucks,
  onClose,
  onSave
}: {
  mode: 'add' | 'edit';
  initial: TruckType | null;
  drivers: ReturnType<typeof useSupplierStore>['drivers'];
  trucks: TruckType[];
  onClose: () => void;
  onSave: (data: Omit<TruckType, 'id'>) => void;
}) {
  const {
    services,
    autoDispatch,
    updateAutoDispatch
  } = useSupplierStore();
  const [plate, setPlate] = useState(initial?.plate || '');
  const [type, setType] = useState(initial?.type || TRUCK_TYPES[0]);
  const [capacity, setCapacity] = useState(initial?.capacity || '');
  const [status, setStatus] = useState<TruckType['status']>(initial?.status || 'Idle');
  const [lastService, setLastService] = useState(initial?.lastService || new Date().toISOString().slice(0, 10));
  const [assignedDriverId, setAssignedDriverId] = useState<string>(initial?.assignedDriverId || '');
  const [driverSearch, setDriverSearch] = useState('');
  const [driverDropdownOpen, setDriverDropdownOpen] = useState(false);
  const driverDropdownRef = useRef<HTMLDivElement>(null);
  // Drivers already assigned to other trucks
  const availableDrivers = drivers.filter(d => !d.assignedTruckId || d.assignedTruckId === initial?.id || d.id === initial?.assignedDriverId);
  const filteredDrivers = availableDrivers.filter(d => d.name.toLowerCase().includes(driverSearch.toLowerCase()));
  const selectedDriver = drivers.find(d => d.id === assignedDriverId);
  // Close driver dropdown on outside click
  useEffect(() => {
    if (!driverDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (driverDropdownRef.current && !driverDropdownRef.current.contains(e.target as Node)) {
        setDriverDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [driverDropdownOpen]);
  // Services for this truck's type (from auto-dispatch capability map)
  const truckServices = autoDispatch.truckServiceMap?.[type] || [];
  const toggleService = (service: string) => {
    const current = autoDispatch.truckServiceMap?.[type] || [];
    const next = current.includes(service) ? current.filter(s => s !== service) : [...current, service];
    updateAutoDispatch({
      truckServiceMap: {
        ...(autoDispatch.truckServiceMap || {}),
        [type]: next
      }
    });
  };
  const handleSubmit = () => {
    if (!plate.trim()) {
      toast.error('Please enter a plate number');
      return;
    }
    if (!capacity.trim()) {
      toast.error('Please enter capacity');
      return;
    }
    onSave({
      plate: plate.trim().toUpperCase(),
      type,
      capacity: capacity.trim(),
      status,
      lastService,
      assignedDriverId: assignedDriverId || undefined
    });
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-4485">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]" data-id="element-4486">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0" data-id="element-4487">
          <div data-id="element-4488">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-4489">
              {mode === 'edit' ? 'Edit Truck' : 'Add Truck'}
            </h3>
            <p className="text-sm text-gray-500" data-id="element-4490">
              {mode === 'edit' ? 'Update truck details and assignments' : 'Register a new vehicle to your fleet'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-4491">
            <X className="w-5 h-5" data-id="element-4492" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4 flex-1" data-id="element-4493">
          <div data-id="element-4494">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4495">
              Plate Number
            </label>
            <input type="text" value={plate} onChange={e => setPlate(e.target.value)} placeholder="e.g., D 12345" className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4496" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4497">
            <div data-id="element-4498">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4499">
                Truck Type
              </label>
              <select value={type} onChange={e => setType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4500">
                {TRUCK_TYPES.map(t => <option key={t} value={t} data-id="element-4501">
                    {t}
                  </option>)}
              </select>
            </div>
            <div data-id="element-4502">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4503">
                Capacity
              </label>
              <input type="text" value={capacity} onChange={e => setCapacity(e.target.value)} placeholder="e.g., 3 Tons" className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4504" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4505">
            <div data-id="element-4506">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4507">
                Status
              </label>
              <select value={status} onChange={e => setStatus(e.target.value as TruckType['status'])} className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4508">
                {STATUS_OPTIONS.map(s => <option key={s} value={s} data-id="element-4509">
                    {s}
                  </option>)}
              </select>
            </div>
            <div data-id="element-4510">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4511">
                Last Service Date
              </label>
              <input type="date" value={lastService} onChange={e => setLastService(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4512" />
            </div>
          </div>

          {/* Searchable Driver Picker */}
          <div ref={driverDropdownRef} className="relative" data-id="element-4513">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4514">
              Assign Driver (optional)
            </label>
            {selectedDriver && !driverDropdownOpen ? <button type="button" onClick={() => {
            setDriverDropdownOpen(true);
            setDriverSearch('');
          }} className="w-full flex items-center gap-3 px-3 py-2 border border-gray-300 rounded-xl text-left hover:border-gray-400 transition-colors" data-id="element-4515">
                <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center font-bold text-sm shrink-0" data-id="element-4516">
                  {selectedDriver.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0" data-id="element-4517">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-4518">
                    {selectedDriver.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate" data-id="element-4519">
                    {selectedDriver.phone} • ★ {selectedDriver.rating}
                  </p>
                </div>
                <button type="button" onClick={e => {
              e.stopPropagation();
              setAssignedDriverId('');
            }} className="p-1 text-gray-400 hover:text-red-500 rounded shrink-0" aria-label="Remove driver" data-id="element-4520">
                  <X className="w-4 h-4" data-id="element-4521" />
                </button>
              </button> : <>
                <div className="relative" data-id="element-4522">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-4523" />
                  <input type="text" value={driverSearch} onChange={e => setDriverSearch(e.target.value)} onFocus={() => setDriverDropdownOpen(true)} placeholder="Search drivers by name..." className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4524" />
                </div>
                <AnimatePresence data-id="element-4525">
                  {driverDropdownOpen && <motion.div initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -4
              }} transition={{
                duration: 0.12
              }} className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg" data-id="element-4526">
                      <button type="button" onClick={() => {
                  setAssignedDriverId('');
                  setDriverDropdownOpen(false);
                }} className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 border-b border-gray-100" data-id="element-4527">
                        Unassigned
                      </button>
                      {filteredDrivers.length === 0 ? <div className="px-3 py-4 text-center text-sm text-gray-500" data-id="element-4528">
                          No matching drivers
                        </div> : filteredDrivers.map(d => {
                  const isCurrent = d.id === assignedDriverId;
                  return <button key={d.id} type="button" onClick={() => {
                    setAssignedDriverId(d.id);
                    setDriverDropdownOpen(false);
                  }} className={`w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-gray-50 ${isCurrent ? 'bg-brand-50/40' : ''}`} data-id="element-4529">
                              <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center font-bold text-xs shrink-0" data-id="element-4530">
                                {d.name.charAt(0)}
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-4531">
                                <p className="text-sm font-medium text-gray-900 truncate" data-id="element-4532">
                                  {d.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate" data-id="element-4533">
                                  {d.phone} • ★ {d.rating}
                                </p>
                              </div>
                              {isCurrent && <span className="text-[10px] font-bold uppercase tracking-wider text-brand-600 shrink-0" data-id="element-4534">
                                  Selected
                                </span>}
                            </button>;
                })}
                    </motion.div>}
                </AnimatePresence>
              </>}
            <p className="text-xs text-gray-500 mt-1" data-id="element-4535">
              Only drivers without another truck are shown.
            </p>
          </div>

          {/* Services Capability */}
          <div data-id="element-4536">
            <div className="flex items-center justify-between mb-1" data-id="element-4537">
              <label className="block text-sm font-medium text-gray-700" data-id="element-4538">
                Capable Services
              </label>
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-4539">
                {truckServices.length} / {services.length} enabled
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-2" data-id="element-4540">
              Select services this truck can perform. Applies to all{' '}
              <span className="font-semibold" data-id="element-4541">{type}</span> trucks.
            </p>
            {services.length === 0 ? <div className="text-xs text-gray-500 text-center py-4 border border-dashed border-gray-200 rounded-xl" data-id="element-4542">
                No services configured yet. Add services in the AI Service
                Advisor tab.
              </div> : <>
                <div className="flex items-center gap-2 mb-2" data-id="element-4543">
                  <button type="button" onClick={() => updateAutoDispatch({
                truckServiceMap: {
                  ...(autoDispatch.truckServiceMap || {}),
                  [type]: [...services]
                }
              })} className="text-xs font-medium text-gray-700 hover:text-gray-900 px-2 py-1 rounded-md hover:bg-gray-100" data-id="element-4544">
                    Select all
                  </button>
                  <button type="button" onClick={() => updateAutoDispatch({
                truckServiceMap: {
                  ...(autoDispatch.truckServiceMap || {}),
                  [type]: []
                }
              })} className="text-xs font-medium text-gray-700 hover:text-gray-900 px-2 py-1 rounded-md hover:bg-gray-100" data-id="element-4545">
                    Clear
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-1" data-id="element-4546">
                  {services.map(service => {
                const isSelected = truckServices.includes(service);
                return <button type="button" key={service} onClick={() => toggleService(service)} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors border ${isSelected ? 'bg-gray-900 text-white border-gray-900 hover:bg-gray-800' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`} data-id="element-4547">
                        {isSelected && <CheckCircle2 className="w-3 h-3" data-id="element-4548" />}
                        {service}
                      </button>;
              })}
                </div>
              </>}
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 shrink-0" data-id="element-4549">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-4550">
            Cancel
          </button>
          <button onClick={handleSubmit} className="px-4 py-2 text-sm font-bold text-white bg-gray-900 hover:bg-gray-800 rounded-xl" data-id="element-4551">
            {mode === 'edit' ? 'Save Changes' : 'Add Truck'}
          </button>
        </div>
      </motion.div>
    </div>;
}
// ============ Assign Driver Modal ============
function AssignDriverModal({
  truck,
  drivers,
  trucks,
  onClose,
  onAssign
}: {
  truck: TruckType;
  drivers: ReturnType<typeof useSupplierStore>['drivers'];
  trucks: TruckType[];
  onClose: () => void;
  onAssign: (driverId: string | null) => void;
}) {
  const [search, setSearch] = useState('');
  const availableDrivers = drivers.filter(d => !d.assignedTruckId || d.assignedTruckId === truck.id);
  const filtered = availableDrivers.filter(d => d.name.toLowerCase().includes(search.toLowerCase()));
  const currentDriver = drivers.find(d => d.id === truck.assignedDriverId);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-4552">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[80vh]" data-id="element-4553">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0" data-id="element-4554">
          <div data-id="element-4555">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-4556">Assign Driver</h3>
            <p className="text-sm text-gray-500" data-id="element-4557">
              Truck {truck.plate} • {truck.type}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-4558">
            <X className="w-5 h-5" data-id="element-4559" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-100 shrink-0" data-id="element-4560">
          <div className="relative" data-id="element-4561">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-4562" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search drivers..." className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-gray-900 focus:border-gray-900" data-id="element-4563" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-4564">
          {currentDriver && <button onClick={() => onAssign(null)} className="w-full text-left px-6 py-3 border-b border-gray-100 hover:bg-red-50 group flex items-center gap-3" data-id="element-4565">
              <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center" data-id="element-4566">
                <X className="w-4 h-4" data-id="element-4567" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-4568">
                <p className="text-sm font-medium text-gray-900" data-id="element-4569">
                  Remove driver
                </p>
                <p className="text-xs text-gray-500 truncate" data-id="element-4570">
                  Currently: {currentDriver.name}
                </p>
              </div>
            </button>}
          {filtered.length === 0 ? <div className="p-8 text-center text-sm text-gray-500" data-id="element-4571">
              No available drivers
            </div> : filtered.map(d => {
          const isCurrent = d.id === truck.assignedDriverId;
          return <button key={d.id} onClick={() => onAssign(d.id)} className={`w-full text-left px-6 py-3 border-b border-gray-100 hover:bg-gray-50 flex items-center gap-3 ${isCurrent ? 'bg-brand-50/50' : ''}`} data-id="element-4572">
                  <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center font-bold text-sm shrink-0" data-id="element-4573">
                    {d.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4574">
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-4575">
                      {d.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate" data-id="element-4576">
                      {d.phone} • ★ {d.rating}
                    </p>
                  </div>
                  {isCurrent && <span className="text-[10px] font-bold uppercase tracking-wider text-brand-600" data-id="element-4577">
                      Current
                    </span>}
                </button>;
        })}
        </div>
      </motion.div>
    </div>;
}
// ============ Confirm Delete Modal ============
function ConfirmDeleteModal({
  truck,
  onCancel,
  onConfirm
}: {
  truck: TruckType;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-4578">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden" data-id="element-4579">
        <div className="p-6" data-id="element-4580">
          <div className="w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-4" data-id="element-4581">
            <Trash2 className="w-5 h-5" data-id="element-4582" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-4583">
            Remove this truck?
          </h3>
          <p className="text-sm text-gray-600" data-id="element-4584">
            Truck <span className="font-bold" data-id="element-4585">{truck.plate}</span> ({truck.type}
            ) will be removed from your fleet. Any active jobs assigned to it
            will be unassigned. This cannot be undone.
          </p>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3" data-id="element-4586">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-4587">
            Cancel
          </button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl" data-id="element-4588">
            Remove Truck
          </button>
        </div>
      </motion.div>
    </div>;
}
// ============ Bulk Upload Modal ============
type ParsedRow = {
  rowNumber: number;
  plate: string;
  type: string;
  capacity: string;
  status: TruckType['status'];
  lastService: string;
  errors: string[];
};
function parseCSV(text: string): string[][] {
  // Simple CSV parser: handles quoted fields with commas
  const rows: string[][] = [];
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  for (const line of lines) {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (c === ',' && !inQuotes) {
        fields.push(current.trim());
        current = '';
      } else {
        current += c;
      }
    }
    fields.push(current.trim());
    rows.push(fields);
  }
  return rows;
}
function BulkUploadModal({
  onClose,
  onImport
}: {
  onClose: () => void;
  onImport: (rows: ParsedRow[]) => void;
}) {
  const [step, setStep] = useState<'upload' | 'preview'>('upload');
  const [fileName, setFileName] = useState<string>('');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleDownloadTemplate = () => {
    const headers = ['plate', 'type', 'capacity', 'status', 'lastService'];
    const sampleRows = [['D 12345', '3-Ton Box Truck', '3 Tons', 'Active', '2024-10-15'], ['S 67890', 'Flatbed 12m', '12m flatbed', 'Idle', '2024-09-20'], ['A 11223', 'Refrigerated 7-Ton', '7 Tons reefer', 'Active', '2024-11-05']];
    const csv = [headers.join(','), ...sampleRows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'fleet-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };
  const handleFileSelect = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const rows = parseCSV(text);
      if (rows.length < 2) {
        toast.error('CSV file is empty or missing data rows');
        return;
      }
      const headers = rows[0].map(h => h.toLowerCase());
      const plateIdx = headers.indexOf('plate');
      const typeIdx = headers.indexOf('type');
      const capacityIdx = headers.indexOf('capacity');
      const statusIdx = headers.indexOf('status');
      const lastServiceIdx = headers.indexOf('lastservice');
      if (plateIdx === -1 || typeIdx === -1 || capacityIdx === -1) {
        toast.error('CSV must include columns: plate, type, capacity, status, lastService');
        return;
      }
      const validStatuses: TruckType['status'][] = ['Active', 'In service', 'Idle', 'Maintenance'];
      const parsed: ParsedRow[] = rows.slice(1).map((row, idx) => {
        const errors: string[] = [];
        const plate = row[plateIdx]?.toUpperCase() || '';
        const type = row[typeIdx] || '';
        const capacity = row[capacityIdx] || '';
        const statusRaw = row[statusIdx] || 'Idle';
        const lastService = row[lastServiceIdx] || '';
        if (!plate) errors.push('Plate required');
        if (!type) errors.push('Type required');
        if (!capacity) errors.push('Capacity required');
        const matchedStatus = validStatuses.find(s => s.toLowerCase() === statusRaw.toLowerCase());
        if (statusRaw && !matchedStatus) {
          errors.push(`Invalid status "${statusRaw}"`);
        }
        return {
          rowNumber: idx + 2,
          plate,
          type,
          capacity,
          status: matchedStatus || 'Idle',
          lastService: lastService || new Date().toISOString().slice(0, 10),
          errors
        };
      });
      setParsedRows(parsed);
      setStep('preview');
    };
    reader.readAsText(file);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };
  const validRows = parsedRows.filter(r => r.errors.length === 0);
  const invalidRows = parsedRows.filter(r => r.errors.length > 0);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-4589">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-4590">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0" data-id="element-4591">
          <div data-id="element-4592">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-4593">
              <Upload className="w-5 h-5 text-blue-600" data-id="element-4594" />
              Bulk Upload Trucks
            </h3>
            <p className="text-sm text-gray-500" data-id="element-4595">
              {step === 'upload' ? 'Upload a CSV file to import multiple trucks at once' : `Review ${parsedRows.length} row${parsedRows.length !== 1 ? 's' : ''} before importing`}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-4596">
            <X className="w-5 h-5" data-id="element-4597" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1" data-id="element-4598">
          {step === 'upload' ? <div className="space-y-6" data-id="element-4599">
              {/* Template download */}
              <div className="flex items-start gap-4 p-4 bg-blue-50/50 border border-blue-100 rounded-xl" data-id="element-4600">
                <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0" data-id="element-4601">
                  <FileSpreadsheet className="w-5 h-5" data-id="element-4602" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-4603">
                  <h4 className="text-sm font-bold text-gray-900" data-id="element-4604">
                    New here? Start with our template
                  </h4>
                  <p className="text-xs text-gray-600 mt-0.5" data-id="element-4605">
                    Download the CSV template with sample rows and the exact
                    column format we expect.
                  </p>
                </div>
                <button onClick={handleDownloadTemplate} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-50 transition-colors" data-id="element-4606">
                  <Download className="w-3.5 h-3.5" data-id="element-4607" />
                  Download
                </button>
              </div>

              {/* Upload area */}
              <div onDragOver={e => {
            e.preventDefault();
            setDragOver(true);
          }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${dragOver ? 'border-blue-400 bg-blue-50/50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-4608">
                <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }} data-id="element-4609" />
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" data-id="element-4610" />
                <p className="text-sm font-bold text-gray-900" data-id="element-4611">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-4612">CSV file, max 5MB</p>
              </div>

              {/* Required columns hint */}
              <div className="text-xs text-gray-500" data-id="element-4613">
                <p className="font-semibold text-gray-700 mb-1" data-id="element-4614">
                  Required columns:
                </p>
                <div className="flex flex-wrap gap-1.5" data-id="element-4615">
                  {['plate', 'type', 'capacity', 'status', 'lastService'].map(c => <span key={c} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-mono" data-id="element-4616">
                        {c}
                      </span>)}
                </div>
              </div>
            </div> : <div className="space-y-4" data-id="element-4617">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-3" data-id="element-4618">
                <div className="p-3 bg-green-50 border border-green-100 rounded-xl" data-id="element-4619">
                  <div className="flex items-center gap-2 mb-1" data-id="element-4620">
                    <CheckCircle2 className="w-4 h-4 text-green-600" data-id="element-4621" />
                    <p className="text-xs font-bold text-green-700 uppercase tracking-wider" data-id="element-4622">
                      Ready to import
                    </p>
                  </div>
                  <p className="text-2xl font-display font-bold text-gray-900" data-id="element-4623">
                    {validRows.length}
                  </p>
                </div>
                <div className={`p-3 border rounded-xl ${invalidRows.length > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`} data-id="element-4624">
                  <div className="flex items-center gap-2 mb-1" data-id="element-4625">
                    <AlertCircle className={`w-4 h-4 ${invalidRows.length > 0 ? 'text-red-600' : 'text-gray-400'}`} data-id="element-4626" />
                    <p className={`text-xs font-bold uppercase tracking-wider ${invalidRows.length > 0 ? 'text-red-700' : 'text-gray-500'}`} data-id="element-4627">
                      Has errors
                    </p>
                  </div>
                  <p className="text-2xl font-display font-bold text-gray-900" data-id="element-4628">
                    {invalidRows.length}
                  </p>
                </div>
              </div>

              <div className="text-xs text-gray-500" data-id="element-4629">
                File:{' '}
                <span className="font-medium text-gray-700" data-id="element-4630">{fileName}</span>
              </div>

              {/* Preview table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-4631">
                <div className="max-h-80 overflow-y-auto" data-id="element-4632">
                  <table className="w-full text-xs" data-id="element-4633">
                    <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider sticky top-0" data-id="element-4634">
                      <tr data-id="element-4635">
                        <th className="px-3 py-2 text-left font-bold" data-id="element-4636">#</th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-4637">Plate</th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-4638">Type</th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-4639">
                          Capacity
                        </th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-4640">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-4641">
                          Issues
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100" data-id="element-4642">
                      {parsedRows.map(row => <tr key={row.rowNumber} className={row.errors.length > 0 ? 'bg-red-50/50' : ''} data-id="element-4643">
                          <td className="px-3 py-2 text-gray-500 font-mono" data-id="element-4644">
                            {row.rowNumber}
                          </td>
                          <td className="px-3 py-2 font-medium text-gray-900" data-id="element-4645">
                            {row.plate || <span className="text-red-500 italic" data-id="element-4646">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-4647">
                            {row.type || <span className="text-red-500 italic" data-id="element-4648">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-4649">
                            {row.capacity || <span className="text-red-500 italic" data-id="element-4650">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-4651">
                            {row.status}
                          </td>
                          <td className="px-3 py-2" data-id="element-4652">
                            {row.errors.length === 0 ? <span className="inline-flex items-center gap-1 text-green-600" data-id="element-4653">
                                <CheckCircle2 className="w-3 h-3" data-id="element-4654" /> OK
                              </span> : <span className="text-red-600" data-id="element-4655">
                                {row.errors.join(', ')}
                              </span>}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>}
        </div>

        <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between items-center shrink-0" data-id="element-4656">
          {step === 'preview' ? <button onClick={() => {
          setStep('upload');
          setParsedRows([]);
          setFileName('');
        }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 bg-gray-100 rounded-xl transition-colors" data-id="element-4657">
              Back
            </button> : <div data-id="element-4658"></div>}
          <div className="flex gap-3" data-id="element-4659">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-4660">
              Cancel
            </button>
            {step === 'preview' && <button onClick={() => onImport(validRows)} disabled={validRows.length === 0} className="px-4 py-2 text-sm font-bold text-white bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-xl" data-id="element-4661">
                Import {validRows.length} truck
                {validRows.length !== 1 ? 's' : ''}
              </button>}
          </div>
        </div>
      </motion.div>
    </div>;
}